/**
 * ProductSample — the "See our craft" block shown on every product detail page.
 * One real, downloadable sample per product. Video for animated-feature,
 * live route for website, author-tier PDFs for the rest.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import BookSessionModal from "@/components/BookSessionModal";

const SAMPLES = {
  "story-treatment": {
    kind: "pdf",
    file: "/samples/story-treatment.pdf",
    title: "THE NINE THREADS — Story Treatment",
    blurb:
      "A full story-treatment sample: logline, tagline, paragraph pitch, treatment, and beat-sheet outline for a proposed high-fantasy series.",
    cta: "Read the treatment",
  },
  screenplay: {
    kind: "pdf",
    file: "/samples/screenplay.pdf",
    title: "NEON REQUIEM — Feature Screenplay",
    blurb:
      "The opening four pages of a 108-page industry-formatted feature. Fade-in, cold open, first character reveal.",
    cta: "Read the pages",
    format_note:
      "Format will be different for every project — our deliverable is .pdf, .fdx (Final Draft), or a .txt / .docx file, whichever the buyer prefers and we can produce.",
  },
  "visual-development": {
    kind: "pdf",
    file: "/samples/visual-development.pdf",
    title: "THE LAST LIGHTHOUSE — Art Bible Excerpt",
    blurb:
      "A one-page excerpt of a full 24-page visual-development document — palette, lighting language, character sheet, world grammar.",
    cta: "Open the bible",
  },
  "animated-feature": {
    kind: "video",
    file: "/samples/animated-feature-teaser.mp4",
    poster: "/samples/animated-feature-poster.jpg",
    title: "THE FORGE REEL — Kling 3.0 Pro Sample",
    blurb:
      "A sixty-second cinematic reel — six ten-second passes across six of our worlds. Painterly volumetric light, 1920×1080, 24fps.",
    cta: "Play the reel",
  },
  website: {
    kind: "route",
    file: "/samples/website",
    title: "OAK & IRON DISTILLING CO. — Live Sample",
    blurb:
      "A full one-page site for a fictional artisan bourbon distillery. Hero, story, product catalogue, press, tasting-request form.",
    cta: "Open the live sample",
  },
  "ad-campaign": {
    kind: "pdf",
    file: "/samples/ad-campaign.pdf",
    title: "THE GILDED CAGE — Launch Sprint",
    blurb:
      "A three-ad opening salvo. Connected-TV 30s, Reels 15s, programmatic retargeting. Brief, scripts, endframes, and media plan. Below: a hero product-video pass.",
    cta: "Read the campaign",
    video: "/samples/product-samples/ad-campaign.mp4",
  },
  "marketing-package": {
    kind: "pdf",
    file: "/samples/marketing-package.pdf",
    title: "HOSTILE TAKEOVER — Q1 Go-To-Market",
    blurb:
      "A ninety-day marketing plan for an eight-person B2B SaaS. Positioning, phased tactics, and a KPI dashboard populated for week one. Below: a ten-second sizzle from the launch storyboard.",
    cta: "Read the plan",
    video: "/samples/product-samples/marketing-package.mp4",
  },
  books: {
    kind: "pdf",
    file: "/samples/books.pdf",
    title: "THE FIRST FIRE — Chapter One",
    blurb:
      "The opening chapter of a full-length novel. Voice, cadence, and craft — the sample most editors ask for.",
    cta: "Read Chapter One",
  },
  ecourses: {
    kind: "pdf",
    file: "/samples/ecourses.pdf",
    title: "THE FORGE METHOD — Module 1, Lesson 1",
    blurb:
      "A shootable lesson script with cold open, section breakdown, homework, and class-channel instructions. Below: a ten-second sizzle for the lesson's opening.",
    cta: "Read the lesson",
    video: "/samples/product-samples/ecourses.mp4",
  },
  "pitch-deck": {
    kind: "pdf",
    file: "/samples/pitch-deck.pdf",
    title: "THE LAST LIGHTHOUSE — 12-Slide Investor Deck",
    blurb:
      "A full investor-ready deck built as a real ask: one-line, universe, IP pipeline, market, traction, team, use of funds. Below: an investor-sizzle pass built to run over the deck opener.",
    cta: "Open the deck",
    video: "/samples/product-samples/pitch-deck.mp4",
  },
  "score-voice": {
    kind: "audio",
    file: "/samples/music/v2_ember_hope.mp3",
    voice: "/samples/music/voice-demo.mp3",
    title: "THE FORGE STUDIO — Score & Voice Demos",
    blurb:
      "A one-minute orchestral cue from the studio and a short narrator demo. Live-instrument register, wordless choir, warm concert-hall mastering. Every score and every voice you buy is composed and recorded bespoke — no library music, no stock voice pack.",
    cta: "Book a session",
  },
};

export default function ProductSample({ slug }) {
  const s = SAMPLES[slug];
  const [bookOpen, setBookOpen] = useState(false);
  if (!s) return null;
  return (
    <section
      className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12"
      data-testid={`product-sample-${slug}`}
    >
      <div className="relative border border-[#ff5a00]/25 bg-gradient-to-br from-[#0b0b0d] to-[#141010] p-8 sm:p-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
          See a real sample of what you'll receive
        </p>
        <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] leading-tight mb-3">
          {s.title}
        </h3>
        <p className="font-body text-[15px] text-[#a1a1aa] mb-6 leading-relaxed max-w-3xl">
          {s.blurb}
        </p>

        {s.kind === "video" && (
          <video
            data-testid={`sample-video-${slug}`}
            controls
            preload="metadata"
            poster={s.poster}
            className="w-full max-w-2xl border border-[#27272a] mb-6 bg-black"
          >
            <source src={s.file} type="video/mp4" />
          </video>
        )}

        {s.kind !== "video" && s.video && (
          <video
            data-testid={`sample-video-${slug}`}
            controls
            preload="metadata"
            className="w-full max-w-2xl border border-[#27272a] mb-6 bg-black"
          >
            <source src={s.video} type="video/mp4" />
          </video>
        )}

        {s.format_note && (
          <p
            data-testid={`sample-format-note-${slug}`}
            className="font-body text-[13px] italic text-[#a1a1aa] mb-6 max-w-2xl leading-relaxed border-l-2 border-[#ff5a00]/40 pl-4"
          >
            {s.format_note}
          </p>
        )}

        {s.kind === "audio" && (
          <div className="space-y-4 mb-6 max-w-2xl">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] mb-2">
                Score demo · 60s · orchestral
              </p>
              <audio
                data-testid={`sample-score-${slug}`}
                controls
                preload="metadata"
                src={s.file}
                className="w-full"
              />
            </div>
            {s.voice && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] mb-2">
                  Voice demo · narrator
                </p>
                <audio
                  data-testid={`sample-voice-${slug}`}
                  controls
                  preload="metadata"
                  src={s.voice}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {s.kind === "route" ? (
            <Link
              to={s.file}
              target="_blank"
              rel="noopener"
              className="font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors"
              data-testid={`sample-cta-${slug}`}
            >
              {s.cta} →
            </Link>
          ) : s.kind === "audio" ? (
            <button
              type="button"
              onClick={() => setBookOpen(true)}
              className="font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors"
              data-testid={`sample-cta-${slug}`}
            >
              {s.cta} →
            </button>
          ) : (
            <>
              <a
                href={s.file}
                target="_blank"
                rel="noopener"
                className="font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors"
                data-testid={`sample-cta-${slug}`}
              >
                {s.cta} →
              </a>
              <a
                href={s.file}
                download
                className="font-mono text-[10px] uppercase tracking-[0.32em] border border-[#ff5a00]/40 text-[#ff5a00] px-6 py-3 hover:bg-[#ff5a00]/10 transition-colors"
                data-testid={`sample-download-${slug}`}
              >
                Download
              </a>
            </>
          )}
        </div>

        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mt-6">
          This is one page of a longer deliverable. Full order ships as described above.
        </p>
      </div>
      <BookSessionModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </section>
  );
}
